/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Entity Note component (universal note component for all entities)
 */
import _ from 'lodash';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { $generateHtmlFromNodes } from '@lexical/html';
import { Button, ButtonProps, Modal, App, Tooltip } from 'antd';
import { $getRoot, LexicalEditor } from 'lexical';
import { Edit, Plus } from '@icon-park/react';

import Editor from 'lib/lexicalEditor/LexicalEditor';
import useUpdateEntityNote from 'hooks/mutate/useUpdateEntityNote';
import useDeleteEntityNote from 'hooks/mutate/useDeleteEntityNote';

// props to handle note
export interface IProps extends ButtonProps {
	entityType: EntityTypeEnum;
	entityId: string;
	noteMeta?: NoteResponseModel;
	iconOnly?: boolean;
	label?: string;
}

const EntityNote: React.FC<IProps> = ({ iconOnly, label = 'Add Note', noteMeta, entityType, entityId, ...restProps }) => {
	const { modal } = App.useApp();
	const existingNote = noteMeta?.note;
	const noteId = noteMeta?.id;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editor, setEditor] = useState<LexicalEditor>();
	const [message, setMessage] = useState<string | null>(existingNote);
	const { mutateAsync: updateNote, isPending: isUpdatingNote } = useUpdateEntityNote();
	const { mutateAsync: deleteNote, isPending: isDeletingNote } = useDeleteEntityNote();

	const disableReset = isUpdatingNote || isDeletingNote;
	const disableSubmit = _.isEmpty(message) || message === existingNote || isDeletingNote;

	// function to set note when it is changed
	useEffect(() => {
		setMessage(existingNote);
	}, [existingNote]);

	// function to handle change in editor
	const handleChange = (val: LexicalEditor) => {
		setEditor(val);
		val?.update(() => {
			const html = $generateHtmlFromNodes(val, null);
			const text = html.match('<img([^>]*[^/])>') ? html : html.replace(/<[^>]+>/g, '').trim();
			setMessage(_.isEmpty(text) ? undefined : html);
		});
	};

	// function to reset editor
	const resetEditor = () => {
		setMessage(existingNote);
		if (editor && !existingNote) {
			editor.update(() => {
				const root = $getRoot();
				root.clear();
			});
		}
	};

	// function to handle success of api
	const handleSuccess = () => {
		setIsModalOpen(false);
		resetEditor();
	};

	// function to handle delete note
	const handleDeleteNote = () => {
		modal.confirm({
			title: 'Delete Note',
			content: 'Are you sure you want to delete this note?',
			okText: 'Yes',
			cancelText: 'Cancel',
			okButtonProps: { danger: true },
			onOk: async () => {
				await deleteNote(
					{ entityType, entityId, noteId },
					{
						onSuccess: () => {
							handleSuccess();
						},
					},
				);
				return true;
			},
		});
	};

	// function to handle save note
	const handleSave = () => {
		updateNote(
			{ entityType, entityId, notePatchModel: { id: noteId, note: message } },
			{
				onSuccess: () => {
					handleSuccess();
				},
			},
		);
	};

	// editor modal
	const renderModal = () => (
		<Modal
			title={'Add Note'}
			open={isModalOpen}
			destroyOnHidden
			onCancel={() => {
				setIsModalOpen(false);
				resetEditor();
			}}
			width={800}
			footer={[
				<div key='footer' className={clsx('flex', { 'justify-between': existingNote, 'justify-end': !existingNote })}>
					{!!existingNote && (
						<Button type='text' danger onClick={handleDeleteNote} disabled={isUpdatingNote}>
							{'Delete'}
						</Button>
					)}
					<div className='flex gap-2'>
						<Button key='cancel' onClick={resetEditor} disabled={disableReset}>
							{'Reset'}
						</Button>
						<Button key='save' type='primary' disabled={disableSubmit} loading={isUpdatingNote} onClick={handleSave}>
							{'Save'}
						</Button>
					</div>
				</div>,
			]}
		>
			<Editor editor={editor} value={message} onChange={handleChange} placeholder='Type your note here...' />
		</Modal>
	);

	// icon button
	if (iconOnly) {
		return (
			<>
				<Tooltip title={'Edit Note'}>
					<Button
						className={clsx({ 'text-btn-hover-to-default': restProps?.type === 'text' })}
						icon={<Edit />}
						onClick={() => setIsModalOpen(true)}
						{...restProps}
					/>
				</Tooltip>

				{renderModal()}
			</>
		);
	}

	// text button
	return (
		<>
			<Button
				type='default'
				icon={<Plus />}
				onClick={() => {
					setIsModalOpen(true);
				}}
				{...restProps}
			>
				{label}
			</Button>
			{renderModal()}
		</>
	);
};

export default EntityNote;
